import React, { useState } from "react";

import { formInitialCtx } from "../../../contexts/FormOnlyEmail.context";
import { EmailOnlyFormProps } from "../../shared/EmailOnlyForm/Email.type";

import EmailOnlyFormProvider from "../../shared/EmailOnlyFormProvider";
import FormSuccessBanner from "../../Banners/FormSuccessBanner";

import { mxpEmailCaptured } from "../../../utils/mixpanelEvents/mixpanelEvents";
import { mailCheck } from "../../SignUpForm/helpers/mailCheck";

import { isValidEmail } from "../../../utils/validations";
import { Image, ImageMediaItemUrl, IterableDataType } from "@types";
import { iterableSubmit } from "../../../utils/iterableSubmit";

import emailCaptureStyles from "./EmailCapture.module.scss";
import { getFormattedTime } from "../../../assets/util-scripts/helpers";

interface EmailCaptureProps {
  emailCaptureBg: {
    localFile: {
      publicURL: Image | ImageMediaItemUrl;
    };
  };
  emailCaptureCopy?: string;
  emailCaptureCtaText?: string;
  emailCaptureCtaMessage?: string;
  articleTitle: string;
  emailPlaceholder: string;
  builderComponent: boolean;
}

type MailCheckInitialStateType = {
  shouldShow: boolean;
  suggestion: {
    address: string;
    domain: string;
    full: string;
  };
};

const EmailCapture: React.FC<EmailCaptureProps> = ({
  emailCaptureBg,
  emailCaptureCopy,
  emailCaptureCtaText,
  articleTitle,
  emailPlaceholder = "Email",
  builderComponent = false,
}) => {
  const [email, setEmail] = useState<string>("");
  const [emailErrorMsg, setEmailErrorMsg] = useState<string>("");
  const [inputError, setInputError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailCaptureSuccess, setEmailCaptureSuccess] = useState(false);
  const mailCheckInitialState: MailCheckInitialStateType = {
    shouldShow: false,
    suggestion: {
      address: "",
      domain: "",
      full: "",
    },
  };

  const [mailCheckState, setMailCheckState] = useState(mailCheckInitialState);

  let errorMsg = {
    invalidEmail: "Please enter a valid email address.",
    emailIsRequired: "This field is required.",
  };

  const onSuggest = suggestion => {
    setMailCheckState({
      shouldShow: inputError,
      suggestion,
    });
  };

  const removeSuggestion = () => {
    setMailCheckState(mailCheckInitialState);
  };

  const onClickSuggestion = (e: React.MouseEvent<HTMLSpanElement>) => {
    setEmail(e.currentTarget.textContent);
    setMailCheckState(mailCheckInitialState);
    setInputError(false);
  };

  const checkEmail = (email: string) => {
    if (email === "") {
      setInputError(true);
      setEmailErrorMsg(errorMsg["emailIsRequired"]);
      setMailCheckState(mailCheckInitialState);
    } else if (!isValidEmail(email)) {
      setInputError(true);
      setEmailErrorMsg(errorMsg["invalidEmail"]);
      setMailCheckState(mailCheckInitialState);
    } else {
      setInputError(false);
      return setEmailErrorMsg("");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    checkEmail(e.target.value);
    mailCheck(email, onSuggest, removeSuggestion);

    return setEmail(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (email === "") throw new Error("emailIsRequired");

      if (!isValidEmail(email)) throw new Error("invalidEmail");
      window.analytics?.identify({
        account_status: "lead",
        email,
      });
      const Digioh_Source = window.location.href;
      const digioh_source = Digioh_Source.replace(/^(?:\/\/|[^/]+)*\//, "");

      const data: IterableDataType = {
        digioh_source,
        Digioh_Source,
        email,
        eventName: "newUserSignup",
        SIGN_UP_SOURCE: `Resources article - ${articleTitle}`,
        gatedContent: {
          SUBMIT_URL: window.location.href,
          SUBMITTED_AT: getFormattedTime(),
        },
      };
      const { status } = await iterableSubmit(data);

      if (status === 200) {
        mxpEmailCaptured(email, emailCaptureCtaText);
        setEmail("");
        setEmailErrorMsg("");
        setInputError(false);
        setIsLoading(false);
        setEmailCaptureSuccess(true);
      }
    } catch (error) {
      const { message } = error;

      setIsLoading(false);
      setInputError(true);
      return setEmailErrorMsg(errorMsg[message] ? errorMsg[message] : "");
    }
  };

  const ctx: EmailOnlyFormProps = {
    ...formInitialCtx,
    buttonText: emailCaptureCtaText,
    email,
    handleChange,
    handleSubmit,
    inputError,
    isLoading,
    placeholder: emailPlaceholder,
  };

  const emailCaptureBackground = `url("${
    builderComponent ? emailCaptureBg : emailCaptureBg.localFile.publicURL
  }")`;

  return (
    <>
      <div
        data-testid="email-capture"
        className={emailCaptureStyles.emailCaptureContainer}
        style={{
          backgroundImage: emailCaptureBackground,
        }}
      >
        <h3
          className={emailCaptureStyles.emailCaptureHeader}
          data-testid="email-capture-header"
        >
          {emailCaptureCopy}
        </h3>

        <EmailOnlyFormProvider ctxInitialVal={ctx} />
        {inputError ? (
          <p className={emailCaptureStyles.errorMsg}>{emailErrorMsg}</p>
        ) : (
          ""
        )}
        {mailCheckState.shouldShow && (
          <p className={emailCaptureStyles.mCheckSuggestion}>
            Did you mean{" "}
            <span onClick={onClickSuggestion}>
              {mailCheckState.suggestion.address}@
              <b>{mailCheckState.suggestion.domain}</b>
            </span>
            ?
          </p>
        )}
      </div>
      {emailCaptureSuccess ? (
        <FormSuccessBanner
          data-testid="email-capture-success-banner"
          handleOnClick={() => setEmailCaptureSuccess(false)}
          bannerText="Thanks you for signing up for our emails!"
        />
      ) : (
        ""
      )}
    </>
  );
};

export default EmailCapture;
